import { useAcceptAccountMarketingConsents } from '@/features/login/api/mutation/accept-marketing-consents';
import {
  type LoginConsentStore,
  useLoginConsentStore,
} from '@/features/login/store/login-consents-store';
import { type JWTToken, useLoginAndStoreData } from '@/hooks/use-customer';
import { ga4_login } from '@/libs/ga4/actions/login';
import { ga4_signUp } from '@/libs/ga4/actions/sign-up';
import { usePasswordObjectSchema } from '@/schemas/schemas';
import type { CustomerType } from '@/types/storage';
import { yupResolver } from '@hookform/resolvers/yup';
import { jwtDecode } from 'jwt-decode';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { isJWT } from 'validator';

export interface NewPasswordForm {
  newPassword: string;
  consent: boolean;
}

export const useNewPasswordForm = (isRequiredConsentAccepted: boolean) => {
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitted, isSubmitting },
    setValue,
  } = useForm<NewPasswordForm>({
    resolver: yupResolver<NewPasswordForm>(usePasswordObjectSchema(), {
      abortEarly: false,
    }),
    defaultValues: {
      consent: isRequiredConsentAccepted,
      newPassword: '',
    },
    mode: 'onChange',
    criteriaMode: 'all',
  });

  useEffect(() => {
    setValue('consent', !!isRequiredConsentAccepted);
  }, [isRequiredConsentAccepted]);

  return {
    handleSubmit,
    register,
    errors,
    isSubmitted,
    isSubmitting,
    setValue,
  };
};

const selector = ({ getConsent }: LoginConsentStore) => ({
  getConsent,
});

export const useLoginBySocialMedia = (redirect: string, tracking?: boolean) => {
  const searchParams = useSearchParams();
  const token = searchParams.get('token');
  const status = searchParams.get('status');
  const provider = searchParams.get('provider');
  const customerType = searchParams.get('userType') as CustomerType;
  const registered = searchParams.get('registered');
  const { loginUserWithSocialMedia } = useLoginAndStoreData(redirect);
  const { getConsent } = useLoginConsentStore(selector);
  const { mutateAsync: updateMarketingConsentsMutateAsync } =
    useAcceptAccountMarketingConsents();

  useEffect(() => {
    (async () => {
      if (
        status === 'success' &&
        token &&
        provider &&
        customerType &&
        isJWT(token)
      ) {
        const marketing = getConsent('marketing');

        if (tracking) {
          const { user_id, email } = jwtDecode<JWTToken>(token).data;
          const trackingFunction =
            registered === 'true' ? ga4_signUp : ga4_login;
          if (marketing) {
            await updateMarketingConsentsMutateAsync({
              email,
              acceptEmail: true,
            });
          }
          trackingFunction({ user_id, method: provider });
        }
        loginUserWithSocialMedia({ token, customerType });
      }
    })();
  }, [token, status, provider, customerType, registered, tracking]);
};
